import { useMemo, useEffect, useContext } from 'react';
import getMessagesByKeys from './getMessagesByKeys';
import useApiReducer from '../useApiReducer';
import { getCachedMessagesByKeys } from './messageCache';
import AuthenticatedRouteLanguageContext from '../AuthenticatedRouteLanguageContext';
import UnauthenticatedRouteLanguageContext from '../UnauthenticatedRouteLanguageContext';
import useIsAuthenticatedRoute from '../useIsAuthenticatedRoute';

/**
 * Hook that returns a list of translated messages for given keys.
 *
 * @typedef {Array} results Array of translated messages corresponding to each key.
 * @typedef {boolean} loading If messages are currently loading.
 * @typedef {string} error Error message, if the fetch was unsuccessful.
 *
 * @param {String[]} keys Array of fully qualified message keys to fetch.
 *      ex: [group.name]
 * @returns {[results, loading, error]}
 *
 * @example
 * const [[noItemsFoundLabel = 'No items found.']] = useMessageService([
 *     'base.general.js_no_item_found',
 * ]);
 * console.log(noItemsFoundLabel); // No items found.
 */
const useMessageService = (keys) => {
    const authenticatedRoute = useIsAuthenticatedRoute();
    const { messageLanguage: authenticatedRouteLanguage } = useContext(
        AuthenticatedRouteLanguageContext,
    );
    const { messageLanguage: unauthenticatedRouteLanguage } = useContext(
        UnauthenticatedRouteLanguageContext,
    );
    const messageLanguage = authenticatedRoute
        ? authenticatedRouteLanguage
        : unauthenticatedRouteLanguage;

    const messages = useMemo(
        () => getCachedMessagesByKeys(keys),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [messageLanguage],
    );
    const shouldFetchMessages = useMemo(
        () => messages.some((result) => result === null),
        [messages],
    );
    const [state, actions] = useApiReducer({
        loading: shouldFetchMessages,
        results: messages,
    });

    const fetchMessages = () => {
        let isCancelled = false;
        actions.loading();
        getMessagesByKeys(keys, messageLanguage)
            .then((results) => {
                if (!isCancelled) {
                    actions.fetchComplete(results);
                }
            })
            .catch((e) => {
                if (!isCancelled) {
                    actions.error(e);
                }
            });

        return () => {
            isCancelled = true;
        };
    };
    useEffect(() => {
        if (shouldFetchMessages) {
            fetchMessages();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messageLanguage, shouldFetchMessages]);
    return [state.results, state.loading, state.error];
};

export default useMessageService;
